import { createSlice } from "@reduxjs/toolkit";
import { ensureArray } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from "../common/reducers/utilities";
import { createEagerSelector, eagerMetaFactory } from "../common/reducers/pendingUpdateSlice";

const compareAccountRequests = (ar1, ar2) => ar1.id === ar2.id;

const accountRequestSlice = createSlice({
    name: 'accountRequests',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload ?? [];
        },
        addOrUpdate: (state, action) => {
            for (const request of ensureArray(action.payload)) {
                const idx = state.data.findIndex(ar => compareAccountRequests(request, ar));
                if (idx > -1) {
                    state.data.splice(idx, 1, request);
                } else {
                    state.data.push(request);
                }
            }
        },
        remove: (state, action) => {
            for (const request of ensureArray(action.payload)) {
                const idx = state.data.findIndex(ar => compareAccountRequests(ar, request));
                if (idx > -1) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => accountRequestSlice.getInitialState());
    }
})

// #region network operations
const eagerMeta = eagerMetaFactory(accountRequestSlice.name);
const fetchAccountRequests = network => network.get('/api/v1/accountRequests', accountRequestSlice.actions.set);
export const createAccountRequest = (network, request) => network.post('/api/v1/accountRequest', request, accountRequestSlice.actions.addOrUpdate, eagerMeta(request.id));
export const updateAccountRequest = (network, request) => network.put('/api/v1/accountRequest', request, accountRequestSlice.actions.addOrUpdate, eagerMeta(request.id));
export const approveAccountRequest = (network, request) => network.post('/api/v1/approveAccountRequest/' + encodeURIComponent(request.id), request, accountRequestSlice.actions.remove);
export const rejectAccountRequest = (network, request) => network.post('/api/v1/rejectAccountRequest/' + encodeURIComponent(request.id), request, accountRequestSlice.actions.remove);
// #endregion

// #region permission checks
export const canCreateRequest = (credentials) => credentials.access.hasFull('administrator');
export const canUpdateRequest = (credentials, request) => credentials.access.hasFull('administrator');
export const canRemoveRequest = (credentials, request) => credentials.access.hasFull('administrator');
// #endregion

// #region selector
const eagerSelector = createEagerSelector(accountRequestSlice.name, { mode: 'array' });
const selectAllBase = state => state.accountRequests.data
const selectAll = eagerSelector(selectAllBase)
// #endregion

// #region listeners
const listen = {
    accountRequestCreated: (request, { dispatch }) => dispatch(accountRequestSlice.actions.addOrUpdate(request)),
    accountRequestUpdated: (request, { dispatch }) => dispatch(accountRequestSlice.actions.addOrUpdate(request)),
    accountRequestRemoved: (request, { dispatch }) => dispatch(accountRequestSlice.actions.remove(request))
}
// #endregion

export const [useAccountRequests] = dataHookFactory(selectAll, { run: fetchAccountRequests, listen });

const reducerDetails = { name: accountRequestSlice.name, reducer: accountRequestSlice.reducer };
export default reducerDetails;